import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import ActionToolbar from '../../components/ui/ActionToolbar';
import IconActionButton from '../../components/ui/IconActionButton';
import FilterPanel from '../../components/ui/FilterPanel';
import FilterActions from '../../components/ui/FilterActions';
import MailingModal from '../../components/modals/MailingModal';
import { mailingApiService } from '../../api/mailingApi';
import { usersAPI } from '../../api/api';
import {
  PlusIcon,
  PencilIcon,
  PrinterIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

// DatePicker Component (SalesCallPage'den alındı)
const DatePicker = ({ value, onChange, placeholder, name }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value || '');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const generateCalendar = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }

    return days;
  };

  const handleDateSelect = (day) => {
    if (day) {
      const formattedDate = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      setSelectedDate(formattedDate);
      onChange({ target: { value: formattedDate } });
      setIsOpen(false);
    }
  };

  const handlePrevMonth = (e) => {
    if (e) e.stopPropagation();
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = (e) => {
    if (e) e.stopPropagation();
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  return (
    <div className="relative" ref={containerRef}>
      <input
        ref={inputRef}
        type="text"
        value={formatDisplayDate(selectedDate)}
        onChange={() => { }}
        onClick={() => setIsOpen(!isOpen)}
        placeholder={placeholder}
        name={name}
        readOnly
        className="w-full px-2 py-1.5 text-sm h-[36px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white cursor-pointer"
      />
      {isOpen && (
        <div className="absolute z-50 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 w-64">
          <div className="flex items-center justify-between mb-2">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              ←
            </button>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {monthNames[currentMonth]} {currentYear}
            </span>
            <button
              type="button"
              onClick={handleNextMonth}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              →
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 mb-1">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
              <div key={day} className="p-0.5 text-xs text-center text-gray-500 dark:text-gray-400 font-medium">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-0.5">
            {generateCalendar().map((day, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleDateSelect(day)}
                disabled={!day}
                className={`
                  p-0.5 text-xs rounded transition-colors
                  ${!day ? 'invisible' : ''}
                  ${day ? 'hover:bg-blue-100 dark:hover:bg-blue-900/30 text-gray-900 dark:text-white' : ''}
                  ${selectedDate && parseInt(selectedDate.split('-')[2]) === day ? 'bg-blue-600 text-white' : ''}
                `}
              >
                {day}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const MailingPlanner = () => {
  const [mailings, setMailings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [selectedMailing, setSelectedMailing] = useState(null);
  const [filters, setFilters] = useState({
    mailing_name: '',
    mailing_goal: '',
    planned_date: '',
    target_market: '',
    b2b_b2c: '',
    visual_design_status: '',
    text_content_status: '',
    responsible_person_text: ''
  });
  const [referenceData, setReferenceData] = useState({
    mailingGoals: [],
    targetMarkets: [],
    users: []
  });

  useEffect(() => {
    loadInitialData();
    loadMailings();
  }, []);

  const loadInitialData = async () => {
    try {
      // Get users from API
      const usersResponse = await usersAPI.getUsers();
      let usersList = [];
      if (usersResponse && usersResponse.success) {
        // Filter users who have ID 10 in department
        usersList = (usersResponse.data || [])
          .filter(u => u.department_id === 10 || u.department === 10)
          .map(u => ({
            ...u,
            name: u.full_name || u.email
          }));
      }

      // Demo referans verileri
      const demoGoals = [
        { id: 1, value: 'kutlama', label: 'kutlama' },
        { id: 2, value: 'özel günün kutlaması', label: 'özel günün kutlaması' },
        { id: 3, value: 'duyuru', label: 'duyuru' },
        { id: 4, value: 'Increase and promote sales', label: 'Increase and promote sales' }
      ];

      const demoMarkets = [
        { id: 1, value: 'Tüm Pazarlar', label: 'Tüm Pazarlar' },
        { id: 2, value: 'BDT', label: 'BDT' },
        { id: 3, value: 'Balkan+Baltık', label: 'Balkan+Baltık' },
        { id: 4, value: 'Müslüman ülkeler', label: 'Müslüman ülkeler' },
        { id: 5, value: 'Rus pazarı', label: 'Rus pazarı' },
        { id: 6, value: 'Türkiye pazarı', label: 'Türkiye pazarı' }
      ];

      setReferenceData({
        mailingGoals: demoGoals,
        targetMarkets: demoMarkets,
        users: usersList
      });
    } catch (err) {
      console.error('Reference data load error:', err);
    }
  };

  const loadMailings = async () => {
    setLoading(true);
    try {
      const response = await mailingApiService.getMailings(filters);
      if (response && response.success) {
        setMailings(response.data || []);
      } else {
        setError(response?.message || 'Error occurred while loading mailings.');
      }
    } catch (err) {
      setError(`Could not connect to server: ${err.message}`);
      console.error('Mailing Load Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      mailing_name: '',
      mailing_goal: '',
      planned_date: '',
      target_market: '',
      b2b_b2c: '',
      visual_design_status: '',
      text_content_status: '',
      responsible_person_text: ''
    });
  };

  const handleSearch = () => {
    loadMailings();
  };

  const handleCreateMailing = () => {
    setSelectedMailing(null);
    setIsModalOpen(true);
  };

  const handleEditMailing = (mailing) => {
    setSelectedMailing(mailing);
    setIsModalOpen(true);
  };

  const handleDeleteMailing = async (id) => {
    if (!window.confirm("Bu kaydı silmek istediğinize emin misiniz?")) return;
    setLoading(true);
    try {
      const response = await mailingApiService.deleteMailing(id);
      if (response.success) {
        loadMailings();
      } else {
        alert(response.message || 'Silme işlemi başarısız.');
      }
    } catch (e) {
      console.error(e);
      alert('Sunucu hatası.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveMailing = async (data) => {
    setLoading(true);
    try {
      let response;

      if (selectedMailing && selectedMailing.id) {
        response = await mailingApiService.updateMailing(selectedMailing.id, data);
      } else {
        response = await mailingApiService.createMailing(data);
      }

      if (response.success) {
        setIsModalOpen(false);
        setSelectedMailing(null);
        loadMailings();
      } else {
        setError(response.message || 'Error occurred during operation.');
      }
    } catch (err) {
      console.error(err);
      setError('Server error occurred.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getStatusBadge = (status) => {
    if (status === 'ready' || status === 'completed') {
      return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">Tamamlandı</span>;
    }
    if (status === 'in_progress') {
      return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">Yapılıyor</span>;
    }
    if (status === 'pending' || status === 'waiting') {
      return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400">Beklemede</span>;
    }
    if (status === 'cancelled') {
      return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">İptal Edildi</span>;
    }
    return <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400">-</span>;
  };

  const filteredMailings = useMemo(() => {
    return mailings.filter(mailing => {
      if (filters.mailing_name && !mailing.mailing_name?.toLowerCase().includes(filters.mailing_name.toLowerCase())) {
        return false;
      }
      if (filters.mailing_goal && mailing.mailing_goal !== filters.mailing_goal) {
        return false;
      }
      if (filters.planned_date && mailing.planned_date !== filters.planned_date) {
        return false;
      }
      if (filters.target_market && mailing.target_market !== filters.target_market) {
        return false;
      }
      if (filters.b2b_b2c && mailing.b2b_b2c !== filters.b2b_b2c) {
        return false;
      }
      if (filters.visual_design_status && mailing.visual_design_status !== filters.visual_design_status) {
        return false;
      }
      if (filters.text_content_status && mailing.text_content_status !== filters.text_content_status) {
        return false;
      }
      if (filters.responsible_person_text && !mailing.responsible_text_name?.toLowerCase().includes(filters.responsible_person_text.toLowerCase())) {
        return false;
      }
      return true;
    });
  }, [mailings, filters]);

  return (
    <div className="space-y-4 md:space-y-6 px-3 sm:px-4 md:px-0">
      <MailingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveMailing}
        initialData={selectedMailing}
        referenceData={referenceData}
      />
      <div className="md:px-6 pt-3 md:pt-4">
        <div className="mb-2">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Mailing Planner
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Marketing mailing planlarını yönetin
          </p>
        </div>
        <ActionToolbar className="mb-4">
          <IconActionButton
            icon={PlusIcon}
            onClick={handleCreateMailing}
            title="Yeni Mailing"
          />
          <IconActionButton
            icon={PrinterIcon}
            onClick={() => window.print()}
            title="Yazdır"
          />
        </ActionToolbar>
      </div>

      {error && (
        <div className="px-6">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
            <button
              onClick={() => setError(null)}
              className="ml-2 text-red-500 hover:text-red-700"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="px-6">
          <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
            Loading data...
          </div>
        </div>
      )}

      <div className="md:px-6">
        <FilterPanel
          title="Filters"
          isOpen={isFiltersOpen}
          onToggle={() => setIsFiltersOpen(!isFiltersOpen)}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
              <div className="space-y-0.5">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Mailing Name</Label>
                <Input
                  value={filters.mailing_name}
                  onChange={(e) => handleFilterChange('mailing_name', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm h-[36px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Mailing Name"
                />
              </div>
              <div className="space-y-0.5">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Mailing Goal</Label>
                <Input
                  value={filters.mailing_goal}
                  onChange={(e) => handleFilterChange('mailing_goal', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm h-[36px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Mailing Goal"
                />
              </div>
              <div className="space-y-0.5">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Planned Date</Label>
                <DatePicker
                  value={filters.planned_date}
                  onChange={(e) => handleFilterChange('planned_date', e.target.value)}
                  placeholder="gg.aa.yyyy"
                  name="planned_date"
                />
              </div>
              <div className="space-y-0.5">
                <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Target Market</Label>
                <Input
                  value={filters.target_market}
                  onChange={(e) => handleFilterChange('target_market', e.target.value)}
                  className="w-full px-2 py-1.5 text-sm h-[36px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Target Market"
                />
              </div>
            </div>

            {isFiltersOpen && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-2">
                <div className="space-y-0.5">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">B2B/B2C</Label>
                  <select
                    value={filters.b2b_b2c}
                    onChange={(e) => handleFilterChange('b2b_b2c', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm h-[36px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Choose</option>
                    <option value="all">All</option>
                    <option value="b2b">B2B</option>
                    <option value="b2c">B2C</option>
                  </select>
                </div>
                <div className="space-y-0.5">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Visual Design Status</Label>
                  <select
                    value={filters.visual_design_status}
                    onChange={(e) => handleFilterChange('visual_design_status', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm h-[36px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Choose</option>
                    <option value="ready">Ready</option>
                    <option value="in_progress">In Progress</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div className="space-y-0.5">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Text Content Status</Label>
                  <select
                    value={filters.text_content_status}
                    onChange={(e) => handleFilterChange('text_content_status', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm h-[36px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Choose</option>
                    <option value="ready">Ready</option>
                    <option value="in_progress">In Progress</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div className="space-y-0.5">
                  <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">Responsible Person (Text)</Label>
                  <Input
                    value={filters.responsible_person_text}
                    onChange={(e) => handleFilterChange('responsible_person_text', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm h-[36px] border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Responsible Person"
                  />
                </div>
              </div>
            )}
            <div className="border-t border-gray-100 dark:border-gray-700 pt-3">
              <FilterActions
                onClear={clearFilters}
                onSearch={handleSearch}
                clearLabel="Clear"
                searchLabel="Search"
              />
            </div>
          </div>
        </FilterPanel>
      </div>

      <div className="md:px-6 pb-4 md:pb-6">
        <Card className="bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="p-4">

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Mailing Name</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Mailing Goal</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Planned Date</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Target Market</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">B2B/B2C</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Theme</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Redirect Link</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Visual Design Status</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Text Content Status</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Responsible Person (Text)</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status in General</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredMailings.length === 0 ? (
                    <tr>
                      <td colSpan="12" className="px-3 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                        No records found.
                      </td>
                    </tr>
                  ) : (
                    filteredMailings.map((mailing) => (
                      <tr
                        key={mailing.id}
                        onClick={() => handleEditMailing(mailing)}
                        className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${selectedMailing?.id === mailing.id ? 'bg-blue-50 dark:bg-blue-900/20' : ''}`}
                      >
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-900 dark:text-white font-medium">{mailing.mailing_name}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">{mailing.mailing_goal || '-'}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">{formatDate(mailing.planned_date)}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">{mailing.target_market || '-'}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">
                          {mailing.b2b_b2c === 'both' ? 'B2B & B2C' : mailing.b2b_b2c === 'b2b' ? 'B2B' : mailing.b2b_b2c === 'b2c' ? 'B2C' : '-'}
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">{mailing.theme || '-'}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">{mailing.redirect_link || '-'}</td>
                        <td className="px-3 py-4 whitespace-nowrap">{getStatusBadge(mailing.visual_design_status)}</td>
                        <td className="px-3 py-4 whitespace-nowrap">{getStatusBadge(mailing.text_content_status)}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-xs text-gray-600 dark:text-gray-300">{mailing.responsible_text_name || '-'}</td>
                        <td className="px-3 py-4 whitespace-nowrap">{getStatusBadge(mailing.general_status)}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-right text-xs">
                          <div className="flex justify-end space-x-2">
                            <IconActionButton
                              icon={PencilIcon}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditMailing(mailing);
                              }}
                              title="Edit"
                              className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                            />
                            <IconActionButton
                              icon={TrashIcon}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMailing(mailing.id);
                              }}
                              title="Delete"
                              className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default MailingPlanner;
